// Tasarımdaki proje kartlarının API'de karşılığı olmayan zengin içeriği
// (kısa metinler, rol, stack, "Nasıl çalışır" akış diyagramları). Admin'den
// gelen projeler link_url içindeki `match` anahtarıyla buraya bağlanır;
// eşleşmeyen projeler diyagramsız, API metinleriyle gösterilir.

export type Lang = 'en' | 'tr';
type Localized = Record<Lang, string>;

export type NodeDef = [number, number, string, string, ('guard' | 'store')?];
export type Step = [string, string, string, ('ok' | 'fail')?];

export interface Scenario {
  label: string;
  steps: Step[];
  result: [string, 'ok' | 'fail'];
}

export interface ScenarioTr {
  label: string;
  caps: string[];
  result: string;
}

export interface DiagramDef {
  stack: string;
  nodes: Record<string, NodeDef>;
  // Kesişen kenarlar için dikey sapma noktası (y), "a>b" anahtarıyla
  routes?: Record<string, number>;
  scenarios: Scenario[];
}

export interface ProjectExtra {
  id: string;
  match: string;
  group: 'work' | 'client' | 'study';
  title: string;
  kind: Localized;
  desc: Localized;
  role: Localized;
  stack: string;
  frame1: string;
  frame2: string;
  caseSlug?: string;
  diagram?: DiagramDef;
  diagramTr?: ScenarioTr[];
}

const DG_TR: Record<string, ScenarioTr[]> = {
  dc: [
    { label: 'Doğrulanmış cevap', caps: ['Soru, kullanıcının Supabase token’ıyla gelir. FastAPI herhangi bir işten önce token’ı doğrular.', 'Jev soruyu yaklaşık 0,4 saniyede sınıflandırır: 10-K kapsamında, yatırım tavsiyesi değil.', 'GPT-5.5 ile çalışan tipli bir PydanticAI agent’ı devralır.', 'Sınırlı bir araç çağırır: pgvector ve tam metin arama, Reciprocal Rank Fusion ile birleştirilir.', 'Pasajlar geri gelir; dönen her parça bu turun atıf izin listesine girer.', 'Agent bir GroundedAnswer döner: [n] işaretli metin ve birebir alıntılar.', 'Kod her alıntıyı kendi parçasıyla karşılaştırır. Jev ve sayısal kontroller bir risk sinyali ekler, engel değil.', 'Cevap atıflarıyla arayüze akar ve Supabase’e kaydedilir.'], result: '✓ Cevap gösterildi, her cümle kaynaklı' },
    { label: 'Yatırım tavsiyesi', caps: ['“NVIDIA hissesi almalı mıyım?” FastAPI’ye ulaşır.', 'Jev bunu 0,93 güvenle yatırım tavsiyesi olarak etiketler.', 'Yaklaşık 0,4 saniyede sabit bir cevap döner. GPT-5.5 hiç çalışmaz, maliyet neredeyse sıfır.'], result: '✓ Sabit cevap · ~$0.00003' },
    { label: 'Uydurma alıntı', caps: ['Apple’ın geliri hakkında bir soru gelir.', 'Jev geçirir: kapsam içinde.', 'GPT-5.5 agent’ı devralır.', 'Raporlarda hibrit arama.', 'Pasajlar döner ve izin listesine girer.', 'Bir alıntı, atıf yapılan sayfada birebir yer almıyor.', 'Cevabın tamamı saklanır. Hiçbir şey gösterilmez ya da kaydedilmez; kullanıcı net bir hata görür.'], result: '✕ Cevap saklandı, tahmin edilmedi' }
  ],
  gf: [
    { label: 'Link kaydet', caps: ['Tarayıcı eklentisinden bir URL gelir. Go API onu doğrular ve tekrar kaydı kontrol eder.', 'PENDING olarak kaydedilir ve API hemen cevap verir. Kimse scraping’i beklemez.', 'Redis’te asynq ile bir iş kuyruğa alınır.', 'Metadata worker sırasıyla Open Graph, Twitter kartları, HTML ve schema.org’u okur.', 'Tek bir Jev çağrısı koleksiyon, etiket ve aksiyon önerir; her biri güven skoruyla.', 'Bu sadece bir öneridir. AI kullanıcının kendi düzenini asla değiştirmez.', 'Kabul edildi: link bir “Oku” görevine dönüşür, hatırlatma worker’ı onu planlar.'], result: '✓ Yer imi hazır, görev planlandı' },
    { label: 'Ücretsiz plan', caps: ['Ücretsiz planda yer imi tarayıcıda IndexedDB’ye kaydedilir. Sunucuda satır yok, API çağrısı yok.', 'Koleksiyonlar, etiketler, arama ve planlayıcı tamamen istemcide çalışır.'], result: '✓ Çevrimdışı çalışır, sunucu maliyeti sıfır' },
    { label: 'Site engelliyor', caps: ['Bir URL gelir.', 'PENDING olarak kaydedilir; kullanıcı hemen kullanabilir.', 'İş kuyruğa alınır.', 'Site crawler’ı engeller.', 'Artan aralıklarla tekrar denenir, sonra dead-letter kuyruğuna düşer. İşler idempotent, tekrar çalıştırmak güvenli.'], result: '✓ FAILED işaretlendi, yer imi yine kullanılabilir' }
  ],
  ks: [
    { label: 'Dekont yükleme', caps: ['Kiracı bir banka dekontu PDF’i yükler. Önce JWT ve rol kontrol edilir.', 'NLP dekontu okur: “kira ödemesi” niyeti, gönderen, tutar, dönem ve daire numarası.', 'Fuzzy eşleştirme kiracıyı ve mülkü bulur.', 'Eşleşme 100 üzerinden 85,5 puan alır.', '80’in üstünde: ödeme otomatik eşleştirilir ve kaydedilir.'], result: '✓ Kira ödendi olarak işaretlendi' },
    { label: 'Belirsiz dekont', caps: ['Başka bir dekont gelir.', 'NLP çıkarabildiğini çıkarır; gönderenin adı farklı yazılmış.', 'Fuzzy eşleştirme olası bir kiracı bulur.', 'Güven skoru 64: tek başına karar vermeye yetmez.', '50 ile 79 arasındaki dekontlar mülk sahibine gider; tek tıkla onaylar ya da reddeder.'], result: '✓ Kontrol mülk sahibinde kalır' },
    { label: 'Aylık kira', caps: ['Her ayın 1’inde Celery beat Redis’e bir iş ekler.', 'Bir Celery worker’ı her aktif sözleşme için bu ayın kira borcunu oluşturur.', 'Borçlar PostgreSQL’e kaydedilir.', 'Her yeni borç için bir bildirim görevi çalışır.', 'Mülk sahipleri ve kiracılar borcu kendi panellerinde görür.'], result: '✓ Kira zamanında, elle iş yok' }
  ],
  dm: [
    { label: 'Katalog gezme', caps: ['Bir ziyaretçi kataloğu açar. Nginx TLS’i sonlandırır ve isteği yönlendirir.', 'Middleware hız sınırını (dakikada 600 istek) ve CORS’u uygular.', 'Bir Fiber handler ürünleri Türkçe ya da İngilizce sunar.', 'Sık kullanılan katalog verisi doğrudan Redis’ten gelir.', 'Cache’te yoksa Bun üzerinden PostgreSQL’e gidilir.', 'Her istek Prometheus’ta ölçülür.'], result: '✓ Hızlı, önbellekli katalog' },
    { label: 'Stok güncelleme', caps: ['İşletme sahibi admin panelinde bir stok değişikliği kaydeder.', 'JWT kontrol edilir; refresh token HTTP-only bir cookie’de durur.', 'Rol bazlı erişim: bu handler’a sadece adminler ulaşır.', 'Stok ve stok hareketi PostgreSQL’e yazılır.', 'Önbellekteki katalog yenilenir; site değişikliği anında gösterir.', 'Stok kritik seviyenin altına düşerse işletme sahibine uyarı gider.'], result: '✓ Site ve stok her zaman senkron' },
    { label: 'Randevu talebi', caps: ['Bir ziyaretçi teslimat randevusu ister.', 'Hız sınırı ve girdi doğrulaması.', 'Handler talebi oluşturur.', 'Durumuyla birlikte kaydedilir: beklemede.', 'İşletme sahibi talebi admin panelinde görür ve onaylar.'], result: '✓ Kaçan talep yok' }
  ],
  ny: [
    { label: 'Gez ve sipariş ver', caps: ['Katalog sunucuda render edilir, mobilde hızlı açılır.', 'Kategori ve taş filtreleri tipli bir Prisma sorgusuna dönüşür.', 'PostgreSQL kategori ağacını ve taş türlerini tutar.', '“Sipariş ver” WhatsApp’ı ürün bilgisi hazır şekilde açar. Bakımı gereken bir ödeme akışı yok.'], result: '✓ Sipariş atölyenin WhatsApp’ına düşer' },
    { label: 'Ürün düzenleme', caps: ['İşletme sahibi NextAuth ile giriş yapar; şifreler bcrypt ile hash’lenir.', 'Her form alanı Zod ile doğrulanır.', 'Yüklenen fotoğraflar sharp ile boyutlandırılır ve sıkıştırılır.', 'Ürün Prisma üzerinden kaydedilir.', 'Bir sonraki istekte sitede yayında.'], result: '✓ Koda dokunmadan güncellendi' }
  ],
  rv: [
    {
      label: 'Sipariş akışı (Saga)',
      caps: [
        'Müşteri Idempotency-Key ile checkout başlatır. Nginx isteği Order servisine iletir.',
        'Sipariş PENDING durumunda açılır; müşteri hemen sipariş takip kodunu alır.',
        'Order servisi Inventory’den REST ile süreli stok rezervasyonu alır.',
        'Sipariş AWAITING_PAYMENT olur; PaymentRequested olayı aynı DB transaction’ında Outbox’a yazılır.',
        'Outbox relay olayı güvenle RabbitMQ kuyruğuna iletir.',
        'Payment servisi olayı idempotent şekilde tüketir ve kart ödemesini tamamlar.',
        'Payment servisi Outbox üzerinden PaymentSucceeded olayını RabbitMQ’ya basar.',
        'Order servisi onayı alır: stok kalıcı düşülür ve sipariş CONFIRMED durumuna geçer.'
      ],
      result: '✓ Sipariş onaylandı, stok kalıcı düşüldü'
    },
    {
      label: 'Ödeme hatası ve telafi',
      caps: [
        'Müşteri yetersiz bakiyeli kartla checkout isteği gönderir.',
        'Sipariş açılır ve Inventory servisinde stok geçici olarak tutulur.',
        'PaymentRequested olayı Outbox üzerinden RabbitMQ’ya iletilir.',
        'Payment servisi kart sağlayıcısından ret cevabı alır.',
        'PaymentFailed olayı RabbitMQ üzerinden Order servisine döner.',
        'Order orkestratörü telafi (compensating transaction) adımını başlatır.',
        'Inventory’deki rezervasyon serbest bırakılır, sepet kurtarılır ve sipariş iptal edilir.'
      ],
      result: '✕ Sipariş iptal edildi, rezerve stok serbest bırakıldı'
    },
    {
      label: 'Kuyruk kesintisi ve onarım',
      caps: [
        'Sipariş durumu ve olay kaydı PostgreSQL’e tek bir transaction ile atomik yazılır.',
        'RabbitMQ veya ağ kesintiye uğrar; anlık mesaj iletimi başarısız olur.',
        'Outbox tablosu, kuyruk ayağa kalkana kadar olayları PostgreSQL’de güvenle saklar.',
        'Outbox worker’ı bağlantı gelince bekleyen olayları sırayla RabbitMQ’ya aktarır.',
        'W3C trace kimliği tüm bu adımları Jaeger’da tek bir trace altında toplar.'
      ],
      result: '✓ Sıfır veri kaybı, otomatik iyileşme'
    }
  ]
};

const DG: Record<string, DiagramDef> = {
  dc: {
    stack: 'React · FastAPI · PydanticAI · OpenAI GPT-5.5 · Jev (TypeSafe AI) · Supabase pgvector',
    nodes: {
      user: [80, 250, 'Analyst', 'React SPA'], api: [240, 250, 'FastAPI', 'Supabase JWT check'],
      jev: [400, 250, 'Jev router', 'TypeSafe AI · ~0.4 s', 'guard'], agent: [560, 250, 'Agent', 'PydanticAI · GPT-5.5'],
      validator: [720, 250, 'Validator', 'code, not AI', 'guard'], search: [560, 85, 'Hybrid search', 'pgvector + FTS · RRF', 'store'],
      risk: [720, 85, 'Risk signal', 'Jev + numeric checks'], fixed: [400, 415, 'Fixed reply', 'agent never runs'],
      withheld: [560, 415, 'Withheld', 'nothing shown or saved'], answer: [720, 415, 'Cited answer', 'streamed, then saved']
    },
    scenarios: [
      { label: 'Grounded answer', steps: [
        ['user', 'api', 'The question arrives with the user’s Supabase token. FastAPI verifies it before doing any work.'],
        ['api', 'jev', 'Jev classifies it in about 0.4 s: inside the 10-K corpus, not investment advice.'],
        ['jev', 'agent', 'A typed PydanticAI agent running GPT-5.5 takes the turn.'],
        ['agent', 'search', 'It calls a bounded tool: pgvector and full-text search, fused with Reciprocal Rank Fusion.'],
        ['search', 'agent', 'Passages come back, and every returned chunk joins this turn’s citation allowlist.'],
        ['agent', 'validator', 'The agent returns a GroundedAnswer: text with [n] markers and verbatim excerpts.'],
        ['validator', 'risk', 'Code checks every excerpt against its chunk. Jev and numeric checks add a risk signal, never a gate.'],
        ['validator', 'answer', 'The answer streams to the UI with its citations and is saved to Supabase.', 'ok']
      ], result: ['✓ Answer shown, every sentence cited', 'ok'] },
      { label: 'Investment advice', steps: [
        ['user', 'api', '“Should I buy NVIDIA stock now?” reaches FastAPI.'],
        ['api', 'jev', 'Jev labels it investment advice with 0.93 confidence.'],
        ['jev', 'fixed', 'A fixed reply goes back in about 0.4 s. GPT-5.5 never runs, so the turn costs almost nothing.', 'ok']
      ], result: ['✓ Fixed reply · ~$0.00003', 'ok'] },
      { label: 'Fabricated quote', steps: [
        ['user', 'api', 'A question about Apple’s revenue arrives.'],
        ['api', 'jev', 'Jev passes it through: in scope.'],
        ['jev', 'agent', 'The GPT-5.5 agent takes the turn.'],
        ['agent', 'search', 'Hybrid search over the filings.'],
        ['search', 'agent', 'Passages return and join the allowlist.'],
        ['agent', 'validator', 'One excerpt does not appear verbatim on its cited page.', 'fail'],
        ['validator', 'withheld', 'The whole answer is withheld. Nothing is shown or stored; the user gets a clear error.', 'fail']
      ], result: ['✕ Answer withheld, not guessed', 'fail'] }
    ]
  },
  gf: {
    stack: 'Go · Fiber · PostgreSQL · Redis · asynq workers · Jev (TypeSafe AI) · Next.js · IndexedDB',
    nodes: {
      ext: [80, 250, 'Save', 'extension · one click'], api: [240, 250, 'Go API', 'Fiber · validate · dedupe'],
      pg: [240, 415, 'PostgreSQL', 'bookmark: PENDING', 'store'], redis: [400, 250, 'Job queue', 'Redis · asynq', 'store'],
      meta: [560, 250, 'Metadata worker', 'OG → Twitter → HTML'], ai: [720, 250, 'AI worker', 'Jev · TypeSafe AI', 'guard'],
      review: [720, 415, 'Suggestion', 'accept · edit · reject'], planner: [560, 415, 'Planner', 'task + reminder'],
      local: [80, 85, 'IndexedDB', 'free tier · in browser', 'store'], retry: [560, 85, 'Retry', 'exponential backoff']
    },
    scenarios: [
      { label: 'Save a link', steps: [
        ['ext', 'api', 'A URL arrives from the browser extension. The Go API validates it and checks for duplicates.'],
        ['api', 'pg', 'Saved as PENDING and the API answers right away. Nobody waits for scraping.'],
        ['pg', 'redis', 'A processing job is queued in Redis with asynq.'],
        ['redis', 'meta', 'The metadata worker reads Open Graph, Twitter cards, HTML and schema.org, in that order.'],
        ['meta', 'ai', 'One Jev call suggests a collection, tags and an action, each with a confidence score.'],
        ['ai', 'review', 'It is only a suggestion. AI never changes the user’s own organization.'],
        ['review', 'planner', 'Accepted: the link becomes a “Read” task and the reminder worker schedules it.', 'ok']
      ], result: ['✓ Bookmark READY, task planned', 'ok'] },
      { label: 'Free tier', steps: [
        ['ext', 'local', 'On the free tier the bookmark is saved to IndexedDB in the browser. No server rows, no API call.'],
        ['local', 'planner', 'Collections, tags, search and the planner all run on the client.', 'ok']
      ], result: ['✓ Works offline, zero server cost', 'ok'] },
      { label: 'Site blocks crawler', steps: [
        ['ext', 'api', 'A URL arrives.'],
        ['api', 'pg', 'Saved as PENDING; the user can already use it.'],
        ['pg', 'redis', 'Job queued.'],
        ['redis', 'meta', 'The site blocks the crawler.', 'fail'],
        ['meta', 'retry', 'Retried with exponential backoff, then dead-lettered. Jobs are idempotent and safe to rerun.', 'fail']
      ], result: ['✓ Marked FAILED, bookmark still usable', 'ok'] }
    ]
  },
  ks: {
    stack: 'Django REST · JWT · PostgreSQL 16 · Redis · Celery worker + beat · NLP + fuzzy matching · Next.js · Docker · Coolify',
    nodes: {
      tenant: [80, 250, 'Tenant', 'Next.js panel'], api: [240, 250, 'Django REST', 'JWT · role check', 'guard'],
      nlp: [400, 250, 'NLP', 'intent + entities'], match: [560, 250, 'Fuzzy match', 'tenant & property'],
      score: [720, 250, 'Confidence', '≥80 auto · 50–79 review', 'guard'], auto: [720, 85, 'Payment recorded', 'matched automatically'],
      owner: [720, 415, 'Owner panel', 'review · approve'], pg: [560, 85, 'PostgreSQL 16', 'leases · debits', 'store'],
      beat: [80, 415, 'Celery beat', '1st of every month'], redis: [240, 415, 'Redis', 'Celery broker', 'store'],
      debits: [400, 415, 'Rent debits', 'Celery worker'], notify: [560, 415, 'Notifications', 'Celery task']
    },
    scenarios: [
      { label: 'Receipt upload', steps: [
        ['tenant', 'api', 'A tenant uploads a bank receipt PDF. JWT and role are checked first.'],
        ['api', 'nlp', 'NLP reads it: intent “rent payment”, sender, amount, period and flat number.'],
        ['nlp', 'match', 'Fuzzy matching finds the tenant and the property.'],
        ['match', 'score', 'The match scores 85.5 out of 100.'],
        ['score', 'auto', 'Above 80: the payment is matched and recorded automatically.', 'ok']
      ], result: ['✓ Rent marked as paid', 'ok'] },
      { label: 'Unclear receipt', steps: [
        ['tenant', 'api', 'Another receipt arrives.'],
        ['api', 'nlp', 'NLP extracts what it can; the sender’s name is spelled differently.'],
        ['nlp', 'match', 'Fuzzy matching finds a likely tenant.'],
        ['match', 'score', 'Confidence 64: not enough to decide alone.', 'fail'],
        ['score', 'owner', 'Between 50 and 79 it goes to the owner, who approves or rejects in one click.', 'ok']
      ], result: ['✓ The owner stays in control', 'ok'] },
      { label: 'Monthly rent', steps: [
        ['beat', 'redis', 'On the 1st of every month Celery beat queues a job in Redis.'],
        ['redis', 'debits', 'A Celery worker creates this month’s rent debit for every active lease.'],
        ['debits', 'pg', 'The debits are stored in PostgreSQL.'],
        ['debits', 'notify', 'A notification task runs for each new debit.'],
        ['notify', 'owner', 'Owners and tenants see the charge in their own panels.', 'ok']
      ], result: ['✓ Rent charged on time, no manual work', 'ok'] }
    ]
  },
  dm: {
    stack: 'Go · Fiber · Bun ORM · PostgreSQL · Redis · JWT (HTTP-only refresh cookie) · Prometheus · Nginx · Next.js',
    nodes: {
      visitor: [80, 165, 'Visitor', 'Next.js site · TR/EN'], admin: [80, 335, 'Owner', 'admin panel'],
      nginx: [240, 250, 'Nginx', 'TLS · reverse proxy'], mw: [400, 250, 'Middleware', 'rate limit · JWT · roles', 'guard'],
      api: [560, 250, 'Go API', 'Fiber handlers'], redis: [720, 165, 'Redis', 'catalog cache', 'store'],
      pg: [720, 335, 'PostgreSQL', 'Bun ORM', 'store'], notify: [560, 415, 'Notifications', 'orders · low stock'],
      metrics: [560, 85, 'Prometheus', 'request metrics']
    },
    scenarios: [
      { label: 'Browse catalog', steps: [
        ['visitor', 'nginx', 'A visitor opens the catalog. Nginx terminates TLS and proxies the request.'],
        ['nginx', 'mw', 'Middleware applies the rate limit (600 requests a minute) and CORS.'],
        ['mw', 'api', 'A Fiber handler serves the products in Turkish or English.'],
        ['api', 'redis', 'Hot catalog data comes straight from Redis.'],
        ['redis', 'pg', 'On a cache miss it falls back to PostgreSQL through Bun.'],
        ['api', 'metrics', 'Every request is measured in Prometheus.', 'ok']
      ], result: ['✓ Fast, cached catalog', 'ok'] },
      { label: 'Owner updates stock', steps: [
        ['admin', 'nginx', 'The owner saves a stock change in the admin panel.'],
        ['nginx', 'mw', 'The JWT is checked; the refresh token lives in an HTTP-only cookie.'],
        ['mw', 'api', 'Role-based access: only admins reach this handler.'],
        ['api', 'pg', 'Stock and the stock movement are written to PostgreSQL.'],
        ['pg', 'redis', 'The cached catalog refreshes, so the website shows the change at once.'],
        ['api', 'notify', 'If stock drops below the critical level, the owner gets an alert.', 'ok']
      ], result: ['✓ Website and stock always in sync', 'ok'] },
      { label: 'Appointment request', steps: [
        ['visitor', 'nginx', 'A visitor requests a delivery appointment.'],
        ['nginx', 'mw', 'Rate limit and input validation.'],
        ['mw', 'api', 'The handler creates the request.'],
        ['api', 'pg', 'Saved with its status: pending.'],
        ['api', 'notify', 'The owner sees it in the admin panel and approves it.', 'ok']
      ], result: ['✓ No missed requests', 'ok'] }
    ]
  },
  ny: {
    stack: 'Next.js 16 · Prisma · PostgreSQL · NextAuth + bcrypt · Zod · sharp · shadcn/ui',
    nodes: {
      shopper: [80, 165, 'Shopper', 'mobile · desktop'], next: [240, 165, 'Next.js 16', 'server-rendered'],
      prisma: [400, 165, 'Prisma', 'typed queries'], pg: [560, 165, 'PostgreSQL', 'categories · stones', 'store'],
      wa: [720, 165, 'WhatsApp', 'order message'], owner: [80, 335, 'Owner', 'admin panel'],
      auth: [240, 335, 'NextAuth', 'bcrypt passwords', 'guard'], zod: [400, 335, 'Zod', 'input validation', 'guard'],
      sharp: [560, 335, 'sharp', 'image optimization']
    },
    routes: { 'next>wa': 62 },
    scenarios: [
      { label: 'Browse & order', steps: [
        ['shopper', 'next', 'The catalog is rendered on the server, fast on mobile.'],
        ['next', 'prisma', 'Filters by category and stone become a typed Prisma query.'],
        ['prisma', 'pg', 'PostgreSQL holds the category tree and stone types.'],
        ['next', 'wa', '“Order” opens WhatsApp with the product already in the message. No checkout to maintain.', 'ok']
      ], result: ['✓ The order lands in the workshop’s WhatsApp', 'ok'] },
      { label: 'Owner edits a product', steps: [
        ['owner', 'auth', 'The owner signs in with NextAuth; passwords are hashed with bcrypt.'],
        ['auth', 'zod', 'Every form field is validated with Zod.'],
        ['zod', 'sharp', 'Uploaded photos are resized and compressed with sharp.'],
        ['zod', 'prisma', 'The product is saved through Prisma.'],
        ['prisma', 'pg', 'Live on the site with the next request.', 'ok']
      ], result: ['✓ Updated without touching code', 'ok'] }
    ]
  },
  rv: {
    stack: 'Go (Fiber, Bun) · Next.js 16 · TypeScript · Tailwind v4 · PostgreSQL (x5) · RabbitMQ · Nginx · Docker · OpenTelemetry · Jaeger · Prometheus · Grafana',
    nodes: {
      client: [80, 250, 'Storefront', 'Next.js 16 · Vercel'],
      gw: [240, 250, 'Gateway', 'Nginx · reverse proxy'],
      order: [400, 250, 'Order Service', 'Go · Saga orchestrator'],
      inv: [560, 110, 'Inventory', 'stock hold / commit'],
      mq: [560, 250, 'RabbitMQ', 'Transactional Outbox', 'store'],
      pay: [720, 250, 'Payment', 'idempotent consumer'],
      pg: [400, 415, 'PostgreSQL', '5 isolated databases', 'store'],
      otel: [720, 415, 'Telemetry', 'Jaeger · Prometheus', 'guard']
    },
    scenarios: [
      {
        label: 'Checkout Saga',
        steps: [
          ['client', 'gw', 'Customer submits checkout with an Idempotency-Key. Nginx proxies to Order service.'],
          ['gw', 'order', 'Order is created in PENDING state; client gets an immediate tracking ID.'],
          ['order', 'inv', 'Synchronous REST call reserves stock with a time-to-live hold.'],
          ['order', 'pg', 'State set to AWAITING_PAYMENT; PaymentRequested event written to Outbox in the same DB transaction.'],
          ['order', 'mq', 'Outbox relay publishes PaymentRequested to RabbitMQ topic exchange.'],
          ['mq', 'pay', 'Payment service consumes the event idempotently and processes the charge.'],
          ['pay', 'mq', 'Payment publishes PaymentSucceeded through its own transactional outbox.'],
          ['mq', 'order', 'Order service consumes success: commits inventory hold and marks order CONFIRMED.', 'ok']
        ],
        result: ['✓ Order confirmed, stock committed', 'ok']
      },
      {
        label: 'Payment failure & compensation',
        steps: [
          ['client', 'gw', 'Customer attempts checkout with insufficient card funds.'],
          ['gw', 'order', 'Order created and stock hold placed in Inventory.'],
          ['order', 'mq', 'PaymentRequested published via Outbox.'],
          ['mq', 'pay', 'Payment provider declines the card transaction.', 'fail'],
          ['pay', 'mq', 'Payment publishes PaymentFailed event with decline reason.'],
          ['mq', 'order', 'Order orchestrator consumes failure and triggers compensation: releases stock hold.'],
          ['order', 'inv', 'Inventory releases reserved stock back to the public catalog.', 'fail']
        ],
        result: ['✕ Order cancelled, reserved stock released', 'fail']
      },
      {
        label: 'Broker outage & recovery',
        steps: [
          ['order', 'pg', 'Order changes and events commit to PostgreSQL atomically. A crash loses no state.'],
          ['order', 'mq', 'RabbitMQ or network suffers a temporary outage; message publishing fails.', 'fail'],
          ['pg', 'order', 'Transactional Outbox retains unpublished events in PostgreSQL until broker recovers.'],
          ['order', 'mq', 'Outbox worker reconnects with exponential backoff and relays pending events.'],
          ['order', 'otel', 'W3C trace context spans the entire recovery; verified across 19 fault-injection E2E tests.', 'ok']
        ],
        result: ['✓ Zero data loss, self-healing recovery', 'ok']
      }
    ]
  }
};

const EXTRAS: ProjectExtra[] = [
  {
    id: 'rv', match: 'rovena', group: 'work', title: 'Rovena',
    kind: { en: 'E-commerce · Distributed Systems · 2026', tr: 'E-Ticaret · Dağıtık Sistemler · 2026' },
    desc: {
      en: 'A distributed luxury fashion marketplace with 5 Go microservices and an editorial Next.js 16 storefront. Coordinates resilient checkouts via Saga and Transactional Outbox over RabbitMQ.',
      tr: '5 bağımsız Go mikroservisi ve editoryal Next.js 16 vitrinine sahip dağıtık e-ticaret platformu. Sipariş akışını RabbitMQ üzerinde Saga ve Transactional Outbox desenleriyle yönetir.',
    },
    role: { en: 'Solo · architecture to deploy', tr: 'Solo · mimariden yayına' },
    stack: 'Go (Fiber, Bun), Next.js 16, TypeScript, Tailwind v4, PostgreSQL, RabbitMQ, Docker, Nginx, OpenTelemetry, Jaeger, Prometheus, Grafana, Supabase',
    frame1: 'rovena.store', frame2: 'checkout · saga',
    diagram: DG.rv, diagramTr: DG_TR.rv,
  },
  {
    id: 'dc', match: 'document-copilot', group: 'work', title: 'Document Copilot',
    kind: { en: 'AI · RAG · 2026', tr: 'AI · RAG · 2026' },
    desc: {
      en: 'Answers questions about 25 SEC 10-K filings in plain English. Every sentence cites a filing and a page, and code checks each quote before the answer is shown.',
      tr: '25 SEC 10-K raporu hakkındaki soruları sade bir dille cevaplar. Her cümle bir rapora ve sayfaya atıf yapar; her alıntı, cevap gösterilmeden önce kodla doğrulanır.',
    },
    role: { en: 'Solo · design to deploy', tr: 'Solo · tasarımdan yayına' },
    stack: 'Python, FastAPI, PydanticAI, OpenAI GPT-5.5, Jev, pgvector, React',
    frame1: 'document-copilot · chat', frame2: 'document-copilot',
    diagram: DG.dc, diagramTr: DG_TR.dc,
  },
  {
    id: 'gf', match: 'guarda', group: 'work', title: 'GuardaFlow',
    kind: { en: 'Product · SaaS · 2026', tr: 'Ürün · SaaS · 2026' },
    desc: {
      en: 'A local-first bookmark manager and planner. Save a page from the browser, file it into nested collections, and turn it into a scheduled task in one click.',
      tr: 'Local-first bir yer imi yöneticisi ve planlayıcı. Tarayıcıdan bir sayfayı kaydet, iç içe koleksiyonlara yerleştir ve tek tıkla takvimde bir göreve dönüştür.',
    },
    role: { en: 'Solo · product, backend and UI', tr: 'Solo · ürün, backend ve arayüz' },
    stack: 'Go, Fiber, PostgreSQL, Redis + asynq, Jev, Next.js',
    frame1: 'guardaflow · planner', frame2: 'collections',
    diagram: DG.gf, diagramTr: DG_TR.gf,
  },
  {
    id: 'ks', match: 'keystrada', group: 'work', title: 'KeyStrada',
    kind: { en: 'PropTech · startup · 2026', tr: 'PropTech · girişim · 2026' },
    desc: {
      en: 'Rent, maintenance and collections in one panel. Owners, their team and tenants see the same month from their own side.',
      tr: 'Kira, bakım ve tahsilat tek panelde. Mülk sahipleri, ekipleri ve kiracılar aynı ayı kendi taraflarından görür.',
    },
    role: { en: 'Co-founder · full-stack development', tr: 'Kurucu ortak · full stack geliştirme' },
    stack: 'Django REST, Celery, PostgreSQL, Redis, Next.js · TÜBİTAK 2209-A',
    frame1: 'keystrada · dashboard', frame2: 'keystrada.vercel.app',
    caseSlug: 'from-mlkta-to-keystrada-a-new-chapter-in-property-management',
    diagram: DG.ks, diagramTr: DG_TR.ks,
  },
  {
    id: 'dm', match: 'durmuslarmakine', group: 'client', title: 'Durmuşlar Makine',
    kind: { en: 'Client · in production', tr: 'Müşteri · canlıda' },
    desc: {
      en: 'Corporate site for a light construction machinery business, with a catalog, gallery and WhatsApp contact, plus an admin panel built from scratch.',
      tr: 'Hafif inşaat makineleri satan bir işletme için katalog, galeri ve WhatsApp iletişimi olan kurumsal site ve sıfırdan geliştirilmiş admin paneli.',
    },
    role: { en: 'Freelance · end to end', tr: 'Freelance · uçtan uca' },
    stack: 'Go (Fiber, Bun), PostgreSQL, Redis, Next.js',
    frame1: 'durmuslarmakine.com', frame2: 'admin · products',
    diagram: DG.dm, diagramTr: DG_TR.dm,
  },
  {
    id: 'ny', match: 'nymuaksesuar', group: 'client', title: 'Nymu Aksesuar',
    kind: { en: 'Client · in production', tr: 'Müşteri · canlıda' },
    desc: {
      en: 'E-commerce showcase for a natural stone and pearl jewelry workshop, with category and stone filters and a WhatsApp order flow. Built solo, design to deploy.',
      tr: 'Doğal taş ve inci takı atölyesi için kategori ve taş filtreli, WhatsApp sipariş akışlı e-ticaret vitrini. Tek başıma, tasarımdan yayına.',
    },
    role: { en: 'Freelance · solo', tr: 'Freelance · solo' },
    stack: 'Next.js 16, Prisma, PostgreSQL, NextAuth',
    frame1: 'nymuaksesuar.com', frame2: 'admin · homepage slider',
    diagram: DG.ny, diagramTr: DG_TR.ny,
  },
  {
    id: 'gk', match: 'gokturk-logistics', group: 'study', title: 'Göktürk Lojistik',
    kind: { en: 'UI study', tr: 'Arayüz çalışması' },
    desc: { en: 'Bilingual marketing site, cinematic scroll', tr: 'İki dilli tanıtım sitesi, sinematik kaydırma' },
    role: { en: '', tr: '' },
    stack: 'Nuxt 3 · GSAP', frame1: '', frame2: '',
  },
  {
    id: 'br', match: 'bursa-website', group: 'study', title: 'Bursa — The Green Capital',
    kind: { en: 'UI study', tr: 'Arayüz çalışması' },
    desc: { en: 'Travel guide with a map route planner', tr: 'Harita tabanlı rota planlayıcılı gezi rehberi' },
    role: { en: '', tr: '' },
    stack: 'Vanilla JS · Leaflet', frame1: '', frame2: '',
  },
];

export function findExtra(linkUrl: string): ProjectExtra | undefined {
  return EXTRAS.find((e) => linkUrl.includes(e.match));
}
